import { useRef, useState } from 'react';
import useAsyncEffect from 'use-async-effect';

import { useDeviceChange } from '../../DevicesSettings/hooks';
import { useConferenceState } from '@hooks/conferenceContext';
import { MytaverseLogger } from '@helpers/logger';
import { getCatchErrorMessage } from '@helpers/error';
import { NavigatorPermissionsService } from '@helpers/permissionsService';

import { IDevicePermissionState } from '@interfaces/permissions';
import { AnalyticsType } from '@interfaces/analytics';
import { useMytaverseEvent } from '@core/dashboard/providers';

export const useInitMicrophonesDevices = () => {
  const [initializing, setInitializing] = useState(true);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [permissionState, setPermissionState] =
    useState<IDevicePermissionState | null>(null);
  const trackMicPermissionsChange = useRef(false);

  const { activeMicroDeviceId, setActiveMicroDeviceId } = useConferenceState();
  const { trackEventAnalytics } = useMytaverseEvent();

  useDeviceChange(setInitializing);

  useAsyncEffect(async () => {
    switch (permissionState) {
      case 'prompt': {
        trackMicPermissionsChange.current = true;

        trackEventAnalytics(AnalyticsType.InputDevicePermissionsPrompt, {
          type: 'mic',
        });

        try {
          await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
          });

          setPermissionState('granted');
        } catch (error) {
          setPermissionState('denied');
        }
        break;
      }
      case 'denied': {
        if (trackMicPermissionsChange.current) {
          trackEventAnalytics(AnalyticsType.InputDevicePermissionsDeny, {
            type: 'mic',
          });
          trackMicPermissionsChange.current = false;
        }

        setDevices([]);
        setInitializing(false);
        break;
      }
      case 'granted': {
        if (!initializing) {
          return;
        }

        if (!navigator.mediaDevices?.enumerateDevices) {
          throw new Error('enumerateDevices() not supported.');
        }

        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const microDevices = (devices || []).filter(
            (device) => device.kind === 'audioinput',
          );

          localStorage.setItem('microDevices', JSON.stringify(microDevices));

          const activeDeviceId =
            microDevices.find(
              ({ deviceId }) => deviceId === activeMicroDeviceId,
            )?.deviceId || microDevices[0]?.deviceId;

          setDevices(microDevices);
          if (activeDeviceId) {
            setActiveMicroDeviceId(activeDeviceId);
          }
          setInitializing(false);

          if (trackMicPermissionsChange.current) {
            trackEventAnalytics(AnalyticsType.InputDevicePermissionsAccept, {
              type: 'mic',
            });
          }
        } catch (error) {
          throw Error(getCatchErrorMessage(error));
        }
        break;
      }
    }
  }, [
    permissionState,
    activeMicroDeviceId,
    initializing,
    setActiveMicroDeviceId,
    trackEventAnalytics,
  ]);

  useAsyncEffect(async () => {
    try {
      const permissionData =
        await NavigatorPermissionsService.checkAndEnsurePermission(
          'microphone',
        );
      setPermissionState(permissionData.state);

      if ('onchange' in permissionData) {
        permissionData.onchange = () => {
          trackMicPermissionsChange.current = true;
          setPermissionState(permissionData.state);
          setInitializing(true);
        };
      }
    } catch (error) {
      setPermissionState('denied');
      MytaverseLogger.error(getCatchErrorMessage(error));
    }
  }, []);

  return {
    initializing,
    devices,
    activeDeviceId: activeMicroDeviceId,
    setActiveDeviceId: setActiveMicroDeviceId,
    permissionState,
  };
};
